import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Camera, CircleDot } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['status'], queryFn: api.status });
  const snap = useMutation({
    mutationFn: api.takeSnapshot,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['snapshots'] });
      qc.invalidateQueries({ queryKey: ['violations'] });
      toast.success('Snapshot recorded');
    },
    onError: () => toast.error('Snapshot failed'),
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-4 max-w-2xl"
    >
      <div>
        <h1 className="text-xl font-semibold text-zinc-100 tracking-tight">Settings</h1>
        <p className="text-[12px] text-zinc-400 mt-0.5">
          Self-hosted dashboard. Connection status, manual snapshot, and preferences.
          API keys live in the server&apos;s <code className="font-mono text-zinc-300">.env</code>;
          they never enter the UI.
        </p>
      </div>

      <section className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 space-y-3">
        <h2 className="text-sm font-medium text-zinc-200">Connection</h2>
        <StatusRow
          label="Mode"
          value={
            <span
              className={cn(
                'text-xs px-2 py-0.5 rounded border',
                status.data?.mode === 'demo'
                  ? 'border-amber-500/40 text-amber-300'
                  : 'border-emerald-500/40 text-emerald-300',
              )}
            >
              {status.data?.mode === 'demo' ? 'Demo' : 'Live'}
            </span>
          }
        />
        <StatusRow
          label="Trading 212"
          value={
            <span className="inline-flex items-center gap-1.5 text-xs">
              <CircleDot
                className={cn(
                  'size-3',
                  status.data?.t212 === 'connected'
                    ? 'text-emerald-400'
                    : status.data?.t212 === 'error'
                      ? 'text-rose-400'
                      : 'text-zinc-400',
                )}
              />
              {status.data?.t212 === 'connected'
                ? 'Connected'
                : status.data?.t212 === 'error'
                  ? 'Error'
                  : 'Not configured'}
            </span>
          }
        />
        <StatusRow
          label="NewsFilter"
          value={
            <span className="inline-flex items-center gap-1.5 text-xs">
              <CircleDot
                className={cn(
                  'size-3',
                  status.data?.newsfilter === 'connected'
                    ? 'text-emerald-400'
                    : 'text-zinc-400',
                )}
              />
              {status.data?.newsfilter === 'connected' ? 'Connected' : 'Not configured'}
            </span>
          }
        />
        <StatusRow
          label="Finnhub"
          value={
            <span className="inline-flex items-center gap-1.5 text-xs">
              <CircleDot
                className={cn(
                  'size-3',
                  status.data?.finnhub === 'connected'
                    ? 'text-emerald-400'
                    : 'text-zinc-400',
                )}
              />
              {status.data?.finnhub === 'connected' ? 'Connected' : 'Demo fixtures'}
            </span>
          }
        />
      </section>

      <section className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 space-y-3">
        <h2 className="text-sm font-medium text-zinc-200">Snapshots</h2>
        <p className="text-xs text-zinc-400">
          Snapshots run automatically every 30 minutes during UK market hours. Take one
          manually any time.
        </p>
        <Button
          variant="primary"
          onClick={() => snap.mutate()}
          disabled={snap.isPending}
        >
          <Camera className="size-4" /> {snap.isPending ? 'Taking…' : 'Take snapshot'}
        </Button>
      </section>

      <section className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 space-y-2">
        <h2 className="text-sm font-medium text-zinc-200">Connecting your account (self-hosted)</h2>
        <p className="text-xs text-zinc-400 leading-relaxed text-pretty">
          This dashboard runs on your own server. To connect a live Trading 212 account, edit the
          server&apos;s <code className="font-mono text-zinc-300">.env</code> and set
          <code className="font-mono text-zinc-300"> DEMO_MODE=false</code> along with
          <code className="font-mono text-zinc-300"> T212_API_KEY</code> and
          <code className="font-mono text-zinc-300"> T212_API_SECRET</code>. Generate the key
          inside the T212 app (Settings → API Beta) with the IP allowlist set to this
          machine&apos;s outbound IP, then restart the server. Optional news sources:
          <code className="font-mono text-zinc-300"> NEWSFILTER_API_KEY</code> (preferred, free
          tier ~50 req/day) and
          <code className="font-mono text-zinc-300"> FINNHUB_API_KEY</code> (fallback +
          company profile / fundamentals). See the project README for full setup.
        </p>
      </section>
    </motion.div>
  );
}

function StatusRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-zinc-900 last:border-b-0">
      <span className="text-xs text-zinc-400">{label}</span>
      <span className="text-xs text-zinc-200">{value}</span>
    </div>
  );
}
